import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';

/**
 * 顯示 PrimeNG 的 ConfirmDialog。
 * 原本想跟 SystemMessageService 寫在一起，但是這樣 SystemMessageService 放到 Interceptor 內會發生錯誤。
 *
 * @export
 * @class SystemConfirmationService
 */
@Injectable({
  providedIn: 'root'
})
export class SystemConfirmationService {

  constructor(private confirmationService: ConfirmationService,
    private translateService: TranslateService) { }

  /**
   * 顯示確認是否刪除的對話框。
   *
   * @param acceptCallback 按下是之後要執行的 function
   * @param hint 要刪除的資料關鍵字
   * @param rejectCallback 按下否之後要執行的 function
   */
  confirmDelete(acceptCallback: Function, hint?: string, rejectCallback?: Function) {
    if (hint) {
      this.confirm('message.confirm.header.confirmation',
        'message.confirm.message.delete-hint', 'pi pi-info-circle',
        acceptCallback, {'hint': hint }, 'message.confirm.button.delete', rejectCallback);
    } else {
      this.confirm('message.confirm.header.confirmation',
        'message.confirm.message.delete', 'pi pi-info-circle',
        acceptCallback, undefined, 'message.confirm.button.delete', rejectCallback);
    }
  }

  /**
   * 顯示確認是否清除的對話框。
   *
   * @param acceptCallback 按下是之後要執行的 function
   * @param hint 要清除的資料關鍵字
   * @param rejectCallback 按下否之後要執行的 function
   */
  confirmClear(acceptCallback: Function, hint?: string, rejectCallback?: Function) {
    if (hint) {
      this.confirm('message.confirm.header.confirmation',
        'message.confirm.message.clear-hint', 'pi pi-info-circle',
        acceptCallback, {'hint': hint }, 'message.confirm.button.clear', rejectCallback);
    } else {
      this.confirm('message.confirm.header.confirmation',
        'message.confirm.message.clear', 'pi pi-info-circle',
        acceptCallback, undefined, 'message.confirm.button.clear', rejectCallback);
    }
  }

  /**
   * 顯示確認是否刪除選取資料的對話框。
   *
   * @param acceptCallback 按下是之後要執行的 function
   * @param num 要刪除的資料筆數
   * @param rejectCallback 按下否之後要執行的 function
   */
  confirmDeleteSelected(acceptCallback: Function, num?: number, rejectCallback?: Function) {
    if (num) {
      this.confirm('message.confirm.header.confirmation',
        'message.confirm.message.delete-selected-num', 'pi pi-info-circle',
        acceptCallback, { 'num': num }, 'message.confirm.button.delete', rejectCallback);
    } else {
      this.confirm('message.confirm.header.confirmation',
        'message.confirm.message.delete-selected', 'pi pi-info-circle',
        acceptCallback, undefined, 'message.confirm.button.delete', rejectCallback);
    }
  }

  /**
   * 顯示確認是否清除選取資料的對話框。
   *
   * @param acceptCallback 按下是之後要執行的 function
   * @param num 要清除的資料筆數
   * @param rejectCallback 按下否之後要執行的 function
   */
  confirmClearSelected(acceptCallback: Function, num?: number, rejectCallback?: Function) {
    if (num) {
      this.confirm('message.confirm.header.confirmation',
        'message.confirm.message.clear-selected-num', 'pi pi-info-circle',
        acceptCallback, { 'num': num }, 'message.confirm.button.clear', rejectCallback);
    } else {
      this.confirm('message.confirm.header.confirmation',
        'message.confirm.message.clear-selected', 'pi pi-info-circle',
        acceptCallback, undefined, 'message.confirm.button.clear', rejectCallback);
    }
  }

  /**
   * 顯示確認是否執行選取資料的對話框。
   *
   * @param acceptCallback 按下是之後要執行的 function
   * @param action 要執行的動作名稱
   * @param num 要處理的資料筆數
   * @param rejectCallback 按下否之後要執行的 function
   */
  confirmExcuteSelected(acceptCallback: Function, action: string, num?: number, rejectCallback?: Function) {
    if (num) {
      this.confirm('message.confirm.header.confirmation',
        'message.confirm.message.selected-num-action', 'pi pi-info-circle',
        acceptCallback, {'num': num, 'action': action }, 'message.confirm.button.excute', rejectCallback);
    } else {
      this.confirm('message.confirm.header.confirmation',
        'message.confirm.message.action', 'pi pi-info-circle',
        acceptCallback, { 'action': action }, 'message.confirm.button.excute', rejectCallback);
    }
  }

  /**
   * 顯示確認變更未儲存的對話框。
   *
   * @param acceptCallback 按下是之後要執行的 function
   * @param rejectCallback 按下否之後要執行的 function
   */
  confirmUnsaved(acceptCallback: Function, rejectCallback?: Function) {
    this.confirm('message.confirm.header.confirmation',
      'message.confirm.message.unsaved', 'pi pi-info-circle',
      acceptCallback, undefined, 'message.confirm.button.leave', rejectCallback);
  }

  /**
   * 顯示確認放棄未儲存的對話框。
   *
   * @param acceptCallback 按下是之後要執行的 function
   * @param rejectCallback 按下否之後要執行的 function
   */
  confirmDiscardChanges(acceptCallback: Function, rejectCallback?: Function) {
    this.confirm('message.confirm.header.confirmation',
      'message.confirm.message.discard-changes', 'pi pi-info-circle',
      acceptCallback, undefined, 'message.confirm.button.discard', rejectCallback);
  }

  /**
   * 顯示確認對話框。
   *
   * @param header 對話框的 Header
   * @param message 要確認的訊息
   * @param iconClass 訊息前 icon 的 style class
   * @param acceptCallback 按下是之後要執行的 function
   * @param parameters 要確認的訊息要傳遞的參數
   * @param acceptLabel 接受按鈕的文字
   * @param rejectCallback 按下否之後要執行的 function
   * @param rejectLabel 拒絕按鈕的文字
   */
  confirm(header: string, message: string, iconClass: string,
    acceptCallback: Function, parameters?: Object, acceptLabel?: string, rejectCallback?: Function, rejectLabel?: string) {

    this.confirmationService.confirm({
      message: this.translateService.instant(message, parameters),
      header: this.translateService.instant(header),
      icon: iconClass,
      acceptLabel: this.translateService.instant(acceptLabel ? acceptLabel : 'message.confirm.button.accept'),
      rejectLabel: this.translateService.instant(rejectLabel ? rejectLabel : 'message.confirm.button.cancel'),
      accept: () => {
        acceptCallback();
      },
      reject: () => {
        if (typeof rejectCallback === 'function') {
          rejectCallback();
        }
      }
    });
  }
}
